import { useContext, useEffect, useState } from "react";
import { UserContext } from "contexts/UserContext";
import { GPUInstanceType } from "types/InstanceTypes";
import { getInstanceLocationsMatching } from "utils/instanceTypes";

export const isNvidiaEmail = (email: string) => {
  return email.trim().toLowerCase().endsWith("@nvidia.com");
};

// Free tier thresholds in cents -- must match backend (internal/entitlement/entity.go).
export const FREE_TIER_MAX_COMPUTE_HOURLY_CENTS = 35;
export const FREE_TIER_MAX_STORAGE_HOURLY_CENTS = 10;

// The backend (dev-plane InstanceTypesModifier) already applies a 20% service charge to
// base_price before returning it to the UI. Setting this to 1.0 makes withServiceCharge
// a no-op so the UI does not double-apply the markup.
// If the backend markup is ever removed, restore this to 1.2.
export const SERVICE_CHARGE_MULTIPLIER = 1.0;

// A price that has already had the service charge (1.2×) applied — safe to display, not safe to mark up again.
declare const __displayPriceBrand: unique symbol;
export type DisplayPrice = number & { readonly [__displayPriceBrand]: true };

// Accepts any raw price input (plain number, API price string, or undefined) but NOT a DisplayPrice.
// undefined is a separate union member because primitives intersected with object types become never.
// The `?: never` constraint on the branded symbol conflicts with DisplayPrice's `[__displayPriceBrand]: true`,
// so TypeScript will reject a DisplayPrice argument at compile time, preventing accidental double-charging.
type RawPriceInput =
  | undefined
  | ((number | string) & { readonly [__displayPriceBrand]?: never });

// Tailwind class for the free-tier "Free" label -- single source of truth for the brand colour.
export const FREE_TIER_COLOR = "text-secondary";

// Keep isFreeTierInstance as the compute check (used for pricing display on instance cards).
export const isFreeTierInstance = (
  priceAmount: string | undefined,
): boolean => {
  const normalizedPrice = priceAmount?.trim();
  if (!normalizedPrice) return false;
  const dollars = Number(normalizedPrice);
  if (!Number.isFinite(dollars)) return false;
  return Math.round(dollars * 100) <= FREE_TIER_MAX_COMPUTE_HOURLY_CENTS;
};

// Checks if total hourly storage cost (gib * pricePerGiBHr) is within the free tier.
// Pass undefined for pricePerGiBHr when pricing data is missing -- returns false (not free)
// rather than treating absent data as $0.
export const isFreeTierStorage = (
  storageGiB: number,
  pricePerGiBHr: number | undefined,
): boolean => {
  if (
    pricePerGiBHr === undefined ||
    !Number.isFinite(pricePerGiBHr) ||
    !Number.isFinite(storageGiB)
  ) {
    return false;
  }
  const totalHourlyCents = Math.round(storageGiB * pricePerGiBHr * 100);
  return totalHourlyCents <= FREE_TIER_MAX_STORAGE_HOURLY_CENTS;
};

// Extracts storage pricing from an instance and storage size in one call.
// pricePerGbHr is 0 when storage is not billed separately (callers use !pricePerGbHr
// to decide whether to show "Included").
// withinFreeTier is true only when pricing data is present and cost is within the threshold.
// Both pricePerGbHr and totalCost are DisplayPrice — already marked up; do not pass to withServiceCharge.
export const getStoragePricing = (
  instance: GPUInstanceType | undefined,
  storageGiB: number,
): {
  pricePerGbHr: DisplayPrice;
  totalCost: DisplayPrice;
  withinFreeTier: boolean;
} => {
  const priceRaw = instance?.supported_storage?.[0]?.price_per_gb_hr?.amount;
  const pricePerGbHr = Number(priceRaw ?? 0);
  const totalCost = storageGiB * pricePerGbHr;
  // Free-tier check uses raw prices so the threshold comparison is unaffected by the service charge.
  const withinFreeTier = isFreeTierStorage(
    storageGiB,
    priceRaw !== undefined ? pricePerGbHr : undefined,
  );
  return {
    pricePerGbHr: (pricePerGbHr * SERVICE_CHARGE_MULTIPLIER) as DisplayPrice,
    totalCost: (totalCost * SERVICE_CHARGE_MULTIPLIER) as DisplayPrice,
    withinFreeTier,
  };
};

export const getBasePriceAmount = (
  instance: GPUInstanceType | undefined,
  location?: string | null,
): string | undefined => {
  if (location && instance?.location_prices?.[location]?.amount) {
    return instance.location_prices[location].amount;
  }
  return instance?.base_price?.amount;
};

// Cheapest price among the instance's locations that fall inside allowedLocations; null/undefined means no filter.
export const getFilteredBasePriceAmount = (
  instance: GPUInstanceType | undefined,
  allowedLocations: Set<string> | null | undefined,
): string | undefined => {
  if (!instance) return undefined;
  if (!allowedLocations) return instance.base_price?.amount;

  const matchingLocations = getInstanceLocationsMatching(
    instance,
    allowedLocations,
  );

  let cheapest: string | undefined;
  for (const loc of matchingLocations) {
    const price = getBasePriceAmount(instance, loc);
    if (
      price !== undefined &&
      (cheapest === undefined || Number(price) < Number(cheapest))
    ) {
      cheapest = price;
    }
  }
  return cheapest ?? instance.base_price?.amount;
};

// Applies the service charge to a single raw price component for display.
// Accepts a plain number, raw API price string (e.g. base_price.amount), or undefined.
// IMPORTANT: Never pass a DisplayPrice here — TypeScript will reject it at compile time.
export const withServiceCharge = (rawPrice: RawPriceInput): DisplayPrice =>
  (Number(rawPrice ?? 0) * SERVICE_CHARGE_MULTIPLIER) as DisplayPrice;

export const roundDisplayPriceToCents = (
  price: DisplayPrice | number,
): DisplayPrice => Number(price.toFixed(2)) as DisplayPrice;

// Total display price (compute + storage, marked up, rounded to cents); do not pass to withServiceCharge.
export const getRunningTotalDisplayPrice = (
  instance: GPUInstanceType | undefined,
  storageGiB = 0,
  location?: string | null,
): DisplayPrice => {
  const compute = withServiceCharge(getBasePriceAmount(instance, location));
  const { totalCost: storage } = getStoragePricing(instance, storageGiB);
  return (roundDisplayPriceToCents(compute) +
    roundDisplayPriceToCents(storage)) as DisplayPrice;
};

// Hook: returns whether the current user is an entitled NVIDIA employee.
// isLoading is true while user data hasn't loaded yet -- callers should
// avoid showing paid prices until loading is false.
// To check compute free-tier status, combine with isFreeTierInstance():
//   const isComputeFree = isEntitled && isFreeTierInstance(instance.base_price?.amount)
export const useFreeTier = () => {
  const userContext = useContext(UserContext);
  const isLoading = userContext.isLoading;
  const isEntitled =
    !isLoading &&
    !!userContext.me?.email &&
    isNvidiaEmail(userContext.me.email);
  return { isEntitled, isLoading };
};

// Hook: returns whether an arbitrary user (by ID) is an entitled NVIDIA employee.
// Use this — not useFreeTier() — when displaying billing for a resource that
// belongs to someone other than the viewer (e.g. an environment created by a
// teammate that you can see in the Team section). Billing follows the resource
// owner, not the viewer.
//
// While the lookup is in flight (cache miss), isEntitled defaults to false so
// the UI does not under-report charges. Once resolved it switches to the truth.
// Subsequent renders for the same userId are cache hits via UserContext.getUser.
const EMPTY_ENTITLEMENTS: Readonly<Record<string, boolean>> = {};

export const useFreeTierForUserIds = (
  userIds: Array<string | undefined>,
): {
  entitlementByUserId: Readonly<Record<string, boolean>>;
  isLoading: boolean;
} => {
  const { getUser } = useContext(UserContext);
  const userIdsKey = JSON.stringify(
    Array.from(
      new Set(userIds.filter((userId): userId is string => Boolean(userId))),
    ).sort(),
  );
  const [resolved, setResolved] = useState<{
    entitlementByUserId: Readonly<Record<string, boolean>>;
    userIdsKey: string;
  }>({ entitlementByUserId: EMPTY_ENTITLEMENTS, userIdsKey: "[]" });

  useEffect(() => {
    const uniqueUserIds = JSON.parse(userIdsKey) as string[];
    if (uniqueUserIds.length === 0) return;

    let cancelled = false;
    Promise.all(
      uniqueUserIds.map(async (id) => {
        try {
          const user = await getUser(id);
          return [
            id,
            Boolean(user?.email && isNvidiaEmail(user.email)),
          ] as const;
        } catch {
          return [id, false] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled) {
        setResolved({
          entitlementByUserId: Object.fromEntries(entries),
          userIdsKey,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [getUser, userIdsKey]);

  if (userIdsKey === "[]") {
    return {
      entitlementByUserId: EMPTY_ENTITLEMENTS,
      isLoading: false,
    };
  }

  const isLoading = resolved.userIdsKey !== userIdsKey;
  return {
    entitlementByUserId: isLoading
      ? EMPTY_ENTITLEMENTS
      : resolved.entitlementByUserId,
    isLoading,
  };
};

export const useFreeTierForUserId = (
  userId: string | undefined,
): { isEntitled: boolean; isLoading: boolean } => {
  const { entitlementByUserId, isLoading } = useFreeTierForUserIds([userId]);
  return {
    isEntitled: userId ? Boolean(entitlementByUserId[userId]) : false,
    isLoading,
  };
};

// Human-readable note derived from the thresholds above -- single source of truth for UI copy.
const freeTierComputeMax = `$${((FREE_TIER_MAX_COMPUTE_HOURLY_CENTS * SERVICE_CHARGE_MULTIPLIER) / 100).toFixed(2)}/hr`;
const freeTierStorageMax = `$${((FREE_TIER_MAX_STORAGE_HOURLY_CENTS * SERVICE_CHARGE_MULTIPLIER) / 100).toFixed(2)}/hr`;
export const FREE_TIER_NOTE = `Compute up to ${freeTierComputeMax} and storage up to ${freeTierStorageMax} are free for NVIDIA employees.`;
